import React, { useMemo } from 'react';
import {
  Box,
  Button,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HistoryIcon from '@mui/icons-material/History';
import CheckIcon from '@mui/icons-material/Check';
import { MODEL_REGISTRY } from '../../config/modelConfig';
import { ChatMarkdown } from './ChatMarkdown';
import { formatCredits } from './helpers';
import { PG } from './theme';

export type CompareLane = {
  id: string;
  content: string;
  model_used?: string | null;
  usage?: { credits_used?: number } | null;
  artifacts?: { kind?: string; url?: string }[] | null;
};

type Props = {
  lanes: CompareLane[];
  selectedId: string;
  onSelect: (id: string) => void;
  streaming?: { model: string; text: string } | null;
  onRestore?: (id: string) => void;
  restoring?: string | null;
  onUseModel?: (model: string) => void;
  currentModel?: string;
};

export const CompareReply: React.FC<Props> = ({
  lanes,
  selectedId,
  onSelect,
  streaming,
  onRestore,
  restoring,
  onUseModel,
  currentModel,
}) => {
  const options = useMemo(() => {
    const list = [...lanes];
    return list;
  }, [lanes]);
  const active = options.find((l) => l.id === selectedId) || options[0];
  const displayText = streaming && !active?.content ? streaming.text : active?.content || '';
  const displayModel = streaming && !active?.content ? streaming.model : active?.model_used;

  if (!active && !streaming) return null;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1.5 }}>
      <Paper
        elevation={0}
        sx={{
          maxWidth: '92%',
          p: 1.5,
          borderRadius: 2,
          bgcolor: PG.assistant,
          color: PG.text,
          border: '1px solid',
          borderColor: PG.accent,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" sx={{ color: PG.accent, fontWeight: 700 }}>
            Compare
          </Typography>
          <Select
            size="small"
            value={active?.id || ''}
            onChange={(e) => onSelect(String(e.target.value))}
            sx={{
              minWidth: 180,
              color: PG.text,
              fontSize: '0.8rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: PG.border },
            }}
          >
            {options.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {MODEL_REGISTRY[l.model_used || '']?.displayName || l.model_used || 'Model'}
                {l.usage?.credits_used != null ? ` · ${formatCredits(l.usage.credits_used)}` : ''}
              </MenuItem>
            ))}
          </Select>
          {streaming && (
            <Typography variant="caption" sx={{ color: PG.muted }}>
              Streaming {MODEL_REGISTRY[streaming.model]?.displayName || streaming.model}…
            </Typography>
          )}
        </Stack>
        <ChatMarkdown text={streaming && selectedId === '__stream__' ? streaming.text : displayText} streaming={Boolean(streaming && selectedId === '__stream__')} />
        {(active?.artifacts || [])
          .filter((a) => a.kind === 'image' && a.url)
          .map((a) => (
            <Box key={a.url} component="img" src={a.url} alt="generated" sx={{ maxWidth: '100%', mt: 1, borderRadius: 1 }} />
          ))}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
          <Typography variant="caption" sx={{ color: PG.muted }}>
            {MODEL_REGISTRY[displayModel || '']?.displayName || displayModel || ''}
            {active?.usage?.credits_used != null ? ` · ${formatCredits(active.usage.credits_used)}` : ''}
          </Typography>
          <Tooltip title="Copy this model's reply">
            <IconButton
              size="small"
              onClick={() => navigator.clipboard.writeText((streaming && selectedId === '__stream__' ? streaming.text : active?.content) || '')}
              sx={{ color: PG.muted }}
            >
              <ContentCopyIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
          {onUseModel && active?.model_used && active.id !== '__stream__' && (
            <Button
              size="small"
              startIcon={<CheckIcon />}
              disabled={active.model_used === currentModel}
              onClick={() => onUseModel(active.model_used || '')}
            >
              {active.model_used === currentModel ? 'Using this model' : 'Use this model'}
            </Button>
          )}
          {onRestore && active && active.id !== '__stream__' && (
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              disabled={restoring === active.id}
              onClick={() => onRestore(active.id)}
            >
              {restoring === active.id ? 'Restoring…' : 'Restore app'}
            </Button>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};
